import * as Yup from 'yup'
import React, {useContext, useState, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {Formik, Form, Field, FormikErrors} from 'formik'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import { serialize } from 'object-to-formdata'
import {getCSSVariableValue} from '@/_metronic/assets/ts/_utils'
import {FormActions, handleErrors} from '@/components/form/'
import {updateProduct, getProductFilters} from '../../../core/_requests'
import {ProductFilters} from '../../../core/_models'
import {ProductContext} from '../../ProductContext'
import {
  ThumbnailCard,
  StatusCard,
  DetailsCard,
  GeneralCard,
  PricingCard,
  MetaCard
} from '../cards/product'


const schema = Yup.object().shape({
  name: Yup.string().min(3, 'Minimum 3 characters').required('Product name is required'),
  sku: Yup.string().min(3, 'Minimum 3 characters').required('SKU is required'),
  description: Yup.string().min(3, 'Minimum 3 characters').required('Description is required'),
  subtitle: Yup.string().min(3, 'Minimum 3 characters').required('Subtitle is required'),
  path: Yup.string().min(3, 'Minimum 3 characters').required('Product url is required'),
  meta: Yup.object().shape({
    title: Yup.string().min(3, 'Minimum 3 characters').required('Meta page title is required'),
    description: Yup.string().min(3, 'Minimum 3 characters').required('Meta description is required'),
    keywords: Yup.array().of(Yup.string()).test({
      message: 'Meta kewords are required',
      test: arr => {
        return (arr?.length || 0) > 0
      }
    })
  }),
  publicPricing: Yup.object().shape({
    amount: Yup.number().min(0.1).required('The public price is required'),
    currency: Yup.string()
      .min(3, '3 characters')
      .max(3)
      .required('The public price currency is required')
  }),
  faceAmount: Yup.number().min(0.1),
  facecurrency: Yup.string().min(2),
  isAlias: Yup.boolean(),
  alias: Yup.string()
    .nullable()
    .when("isAlias", {
      is: true,
      then: (schema) => schema.required("Product alias is required"),
      otherwise: (schema) => schema.nullable(),
    }),
})

const ProductForm = () => {
  const navigate = useNavigate()
  const MySwal = withReactContent(Swal)

  const {product, setProduct} = useContext(ProductContext)
  const [options, setOptions] = useState<ProductFilters|undefined>()

  useEffect(() => {
    const load = async () => {
      setOptions(await getProductFilters())
    }
    void(options === undefined && load())
  }, [options])


  const onSuccess = () => {
    MySwal.fire({
      title: 'Your modifications have been validated!',
      icon: 'success',
      confirmButtonColor: getCSSVariableValue('--bs-success')
    })
  }


  const onError = <Values extends object>(
    e: unknown,
    setErrors: (errors: FormikErrors<Values>) => void
  ) => {
    const errors = handleErrors(e, setErrors)
    MySwal.fire({
      title: 'Please fix the following errors',
      html: (
        <ul className="d-flex flex-column">
          {errors?.map(([key, value]) => {
            return (
              <li key={`error-${key}`} className="d-flex align-items-center py-2 text-danger">
                <span className="bullet bg-danger me-5"></span>
                {value}
              </li>
            )
          })}
        </ul>
      ),
      icon: 'error',
      confirmButtonColor: getCSSVariableValue('--bs-danger')
    })
  }

  return (
    <Formik
      initialValues={{...product, coverFile: null, isAlias: product?.alias !== null}}
      validationSchema={schema}
      onSubmit={async (values, {setErrors}) => {
        const v = {...values}
        delete v.brand

        // dirty hook for pushing brand._id instead of  brand object 
        const formData = serialize(v)
        
        if (values.brand) {
          formData.append('brand', values.brand._id as string)
        }

        try {
          setProduct(await updateProduct(formData))
          onSuccess()
        } catch (e) {
          onError(e, setErrors)
        }
      }}
    >
      {() => (
        <Form id="product-form" className='form d-flex flex-column flex-lg-row'>
          <div className="d-flex flex-column gap-7 gap-lg-10 w-100 w-lg-300px mb-7 me-lg-10">
            <ThumbnailCard />
            <StatusCard statuses={options?.statuses || []}/>
            <DetailsCard
              brands={options?.brands || []}
              types={options?.types || []}
              categories={options?.categories || []}
              platforms={options?.platforms || []}
            />
          </div>
          <div className="d-flex flex-column flex-row-fluid gap-7 gap-lg-10">
            <GeneralCard />
            <PricingCard />
            <MetaCard />
            <Field
              component={FormActions}
              padding="pt-0"
              position="end"
              close={() => navigate('/p2c/product/catalog')}
            />
          </div>
        </Form>
      )}
    </Formik>
  )
}

export {ProductForm}
